// The handshake the Perfusion Companion app needs to upload scans for a perfusion (ENG-7381).
//
// The workspace can't hand the app a session, so it hands it the signing keys of the user who
// generated the code, encoded into a QR code the app scans. The app signs its own requests with
// them, the same way this client does in `apiClient.signingString`.
//
// Those keys authorise anything that user can do, for as long as they are valid — the code is a
// credential, not a scoped grant, and must be treated like one.

import type { Keys } from '@/models/login';
import { LocalStorageUtils } from '@/utils';
import { BACKEND_URL } from './apiClient';

/** Bumped when the shape changes, so the app can reject codes it doesn't understand. */
export const PERFUSION_SCAN_QR_VERSION = 2;

export interface PerfusionScanQrPayload {
  version: number;
  /**
   * Which deployment the app should talk to — the code is environment-specific. A `localhost` dev
   * url is rewritten to this machine's LAN address, see `resolveScanApiBaseUrl`.
   */
  apiBaseUrl: string;
  /** The investigation (study) the perfusion belongs to, `:id` on the companion endpoints. */
  pumpCenterId: string;
  /** Title of that study, so the app can name what it is scanning. Display only. */
  studyTitle: string;
  /** The case the button was clicked on, `:perfusionId` on the companion endpoints. */
  perfusionId: string;
  /** Label of that case, the workspace's own name for the perfusion. Display only. */
  perfusionLabel: string;
  /** Where the app uploads a scan. It signs this itself, so it is a target, not a grant. */
  request: {
    method: string;
    path: string;
  };
  /**
   * The cached user keys, verbatim: `key` is the key id and `data` the HMAC secret that
   * `signingString` needs. The rest of the stored object rides along.
   */
  keys: Keys;
}

/** Path of the companion scan upload, relative to `apiBaseUrl`. */
export const perfusionScanUri = (pumpCenterId: string, perfusionId: string) =>
  `pump/${pumpCenterId}/case/${perfusionId}/scan`;

/** Hosts that only ever resolve back to the machine that asks for them. */
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1'];

const isLoopbackHost = (host: string) =>
  LOOPBACK_HOSTS.includes(host.toLowerCase()) || host.toLowerCase().startsWith('127.');

/**
 * The API url to put in the code, which has to be reachable from the phone doing the scanning.
 *
 * In development VITE_APP_BACKEND_URL is `http://localhost:3001`, and a phone resolves `localhost`
 * to itself — so the host is swapped for this machine's LAN address, keeping the scheme, port and
 * path intact. Anything already pointing at a real host is left alone.
 *
 * The signature in the payload covers the request line, not the host, so rewriting it here does
 * not invalidate the authorization the app sends back.
 */
export const resolveScanApiBaseUrl = (
  baseUrl: string,
  lanHost: string = import.meta.env.VITE_APP_LAN_HOST ?? '',
  // Falls back to how the workspace itself was opened: browsing via the machine's IP means that
  // address is routable, even when vite couldn't work out which interface to use.
  pageHost: string = typeof window === 'undefined' ? '' : window.location.hostname
): string => {
  let url: URL;
  try {
    url = new URL(baseUrl);
  } catch {
    // Not a url we can rewrite (a relative base, say) — hand it back untouched.
    return baseUrl;
  }

  if (!isLoopbackHost(url.hostname)) {
    return baseUrl;
  }

  const replacement = [lanHost, pageHost].find(host => !!host && !isLoopbackHost(host));
  if (!replacement) {
    return baseUrl;
  }

  url.hostname = replacement;
  // `URL` keeps a trailing slash on an origin-only url; the payload's paths are joined with '/'.
  return url.toString().replace(/\/$/, '');
};

/**
 * Builds the payload encoded into the perfusion scan QR code.
 *
 * Throws when the user has no cached keys — the app cannot sign anything without them, and callers
 * show that rather than encoding a code the app would be rejected on.
 */
export const buildPerfusionScanQrPayload = (
  pumpCenterId: string,
  perfusionId: string,
  // Titles are what the operator reads on the phone to confirm they scanned the right case. They
  // are not identifiers, so a missing one degrades to an empty string rather than failing the code.
  studyTitle: string = '',
  perfusionLabel: string = ''
): PerfusionScanQrPayload => {
  if (!pumpCenterId || !perfusionId) {
    throw new Error('A pump center and a perfusion are both needed to build a scan code.');
  }

  const keys = LocalStorageUtils.getUserKeys();
  if (!keys?.key || !keys?.data) {
    throw new Error('Signing keys are missing — sign in again and retry.');
  }

  const uri = perfusionScanUri(pumpCenterId, perfusionId);

  return {
    version: PERFUSION_SCAN_QR_VERSION,
    apiBaseUrl: resolveScanApiBaseUrl(BACKEND_URL),
    pumpCenterId,
    studyTitle,
    perfusionId,
    perfusionLabel,
    request: {
      method: 'POST',
      path: `/${uri}`
    },
    keys
  };
};
